import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';

interface Module {
  id: string;
  name: string;
  code: string;
  scope: string;
  isActive: boolean;
}

interface SchoolModule {
    id: string;
    enabled: boolean;
    module: {
      id: string;
      name: string;
      code: string;
      scope: string;
    };
  }

/**
 * Module Assignment Component
 * Allows SuperAdmin to enable/disable modules for a school
 */
const ModuleAssignment: React.FC<{ schoolId: string; onUpdate: () => void }> = ({
  schoolId,
  onUpdate,
}) => {
  const [allModules, setAllModules] = useState<Module[]>([]);
  const [schoolModules, setSchoolModules] = useState<SchoolModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [schoolId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [modulesResponse, schoolModulesResponse] = await Promise.all([
        axios.get('/modules'),
        axios.get(`/modules/school/${schoolId}`),
      ]);

      setAllModules(modulesResponse.data.filter((m: Module) => m.scope === 'SCHOOL'));
      setSchoolModules(schoolModulesResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleId: string, enabled: boolean) => {
    setError('');
    setLoading(true);
    
    try {
      // Step 1: Persist to backend FIRST - backend is single source of truth
      await axios.patch(`/schools/${schoolId}/modules/${moduleId}`, {
        enabled,
      });

      // Step 2: ALWAYS refetch from backend after mutation - no optimistic updates
      await fetchData();
      
      // Step 3: Trigger parent update (which calls refreshUser)
      onUpdate();
      
      // Step 4: Trigger dashboard refresh
      window.dispatchEvent(new Event('schoolUpdated'));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update module');
      // Refetch anyway to ensure UI matches backend state
      await fetchData();
    } finally {
      setLoading(false);
    }
  };  

  const isModuleEnabled = (moduleId: string): boolean => {
    return schoolModules.some(
      (sm: any) =>
        sm.enabled === true &&
        sm.module &&
        sm.module.id === moduleId
    );
  };  

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Enable/Disable Modules for School
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Only SCHOOL-scoped modules can be enabled for schools. GLOBAL modules are platform-level only.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Module Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allModules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No modules available
                </TableCell>
              </TableRow>
            ) : (
              allModules.map((module) => (
                <TableRow key={module.id}>
                  <TableCell>{module.name}</TableCell>
                  <TableCell>
                    <Chip label={module.code} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={module.scope} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={isModuleEnabled(module.id)}
                      onChange={(e) => handleToggleModule(module.id, e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ModuleAssignment;

